/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { map } from 'lodash';
import { Field } from 'react-final-form';
import { Dropdown, Text, isEmptyValue } from '@blend/enterprise-components';
import { useQuery } from 'react-query';

import { WrappedInput } from 'id-components/WrappedInput';
import { respondToAuthChallenge, getChallengeMetadata } from 'id-api/auth';
import { FormCard } from 'id-components/FormCard';
import { FinalFormInputConfig } from 'id-types/helpers';
import { useLoginFlowSubmit } from 'id-utils/login';
import { PRINCIPAL_TYPES_TO_HUMAN_READABLE_NAMES } from 'id-constants/identity';

export const SelectPrincipalPage: React.FC = () => {
  const onSubmit = useLoginFlowSubmit<Identity.API.SelectPrincipalForm, Identity.API.SelectPrincipalChallenge>(
    respondToAuthChallenge,
  );

  const { isLoading, data, error } = useQuery<Identity.API.GetPrincipalsForPrincipalSelectionResponse, Error>({
    queryKey: 'getPrincipalsForPrincipalSelection',
    queryFn: async () => {
      const errMessage =
        'There was a problem fetching your principals. Please try clearing your cookies and logging in again. If the issue persists, reach out to support@blend.com.';

      try {
        const res = await getChallengeMetadata();
        return res.data;
      } catch {
        throw new Error(errMessage);
      }
    },
  });

  return (
    <FormCard<Identity.API.SelectPrincipalForm>
      headerLabel={<Text>Please select the role you'd like to use.</Text>}
      headerTitle="Select role"
      id="it-SelectPrincipal"
      onSubmit={onSubmit}
      submitLabel="Confirm"
      errorMessage={error?.message}
    >
      <Field
        {...({
          autoFocus: true,
          component: WrappedInput,
          inputComponent: Dropdown,
          placeholder: isLoading ? 'Loading...' : 'Select',
          customComponentProps: {
            options: map(data?.principals, principal => ({
              text: `${
                (PRINCIPAL_TYPES_TO_HUMAN_READABLE_NAMES[principal.type] || principal.type) +
                (!!principal.description ? `: ${principal.description}` : '')
              }`,
              value: principal.id,
            })),
          },
          label: 'Role',
          labelSize: 'sm',
          name: 'principalId',
          validate: isEmptyValue('Please select a role.'),
        } as FinalFormInputConfig<Dropdown.Props, string>)}
      />
    </FormCard>
  );
};
